import React, { useContext, useEffect, useState, useMemo, memo } from "react";
import Navbar from "./blocks/Navbar.js";
import { Box, Tabs, Tab, Typography, Icon, Link } from "@mui/material";
import {
  useNavigate,
  useSearchParams,
  useParams,
  useLocation,
} from "react-router-dom";
import { useSnackbarContext } from "src/utils/SnackbarContext.js";
import ActionCard from "./blocks/ActionCard.js";
import Event from "./blocks/Event.js";
import Hero from "./blocks/Hero.js";
import TagsCollection from "./blocks/tags/TagsCollection.js";
import LoadingAnimation from "./blocks/LoadingAnimation.js";
import { AuthContext } from "src/utils/AuthContext.js";
import { getPageRoute } from "src/services";
import InfiniteScrollList from "./blocks/InfiniteScrollList.js";
import { useTrendingContent } from "src/utils/useTrendingContent.js";
import { revealNavbar, safeParse } from "src/utils/Utils.js";
import { CalendarSection } from "./blocks/CalendarSection.js";
import SearchBar from "./blocks/SearchBar.js";
import { Helmet } from "react-helmet";
import onboardingTags from "src/onboarding-tags.json";
import { useUpdateTags } from "src/services/mutations/useUpdateTags.js";

let LAST_URL_TAG_ADDED = "";
// Extracted common components
const LoadingState = () => <LoadingAnimation />;

const SignInCard = ({ navigate, actionCardSx, tab }) => {
  const messages = {
    foryou: {
      title: "You're not signed in",
      description: "You'll need to sign in to see your saved tags.",
    },
    upcoming: {
      title: "You're not signed in",
      description: "You'll need to sign in to see your upcoming events.",
    },
  };

  const { title, description } = messages[tab] || messages.foryou;

  return (
    <ActionCard
      title={title}
      description={description}
      testid="guest-need-to-sign"
      primaryButtonCTA="Sign in"
      primaryAction={() => navigate(getPageRoute("auth", "SIGNIN"))}
      iconName="account_circle_off"
      sx={actionCardSx}
    />
  );
};

const CalendarSections = ({
  leaderboardCalendars,
  subscriberAZCalendars,
  isLoadingCalendarsByTags,
  onSubscribeAction,
  setAddedCalendars,
  setShowRecommendedCalendars,
  fetchSearchByTagsNextPage,
  hasMoreSearchByTagsPages,
  tagCalendarsListSx,
  navigate,
}) => (
  <>
    {isLoadingCalendarsByTags && <LoadingAnimation sx={{ p: 0 }} />}
    {leaderboardCalendars?.length > 0 && !isLoadingCalendarsByTags && (
      <>
        <Typography variant="h2" sx={{ mt: 3, mb: 2 }}>
          Leaderboard
        </Typography>
        <CalendarSection
          id="Leaderboard"
          calendarSection={{ calendars: leaderboardCalendars }}
          onSubscribeAction={onSubscribeAction}
          setAddedCalendars={setAddedCalendars}
          setShowRecommendedCalendars={setShowRecommendedCalendars}
          sx={tagCalendarsListSx}
          fetchSearchByTagsNextPage={fetchSearchByTagsNextPage}
        />
      </>
    )}
    {subscriberAZCalendars && !isLoadingCalendarsByTags && (
      <>
        {subscriberAZCalendars.length ? (
          <>
            <Typography variant="h2" sx={{ mt: 3, mb: 2 }}>
              A-Z
            </Typography>
            <CalendarSection
              id="AZCalendars"
              infiniteScrollList={true}
              calendarSection={{ calendars: subscriberAZCalendars }}
              onSubscribeAction={onSubscribeAction}
              setAddedCalendars={setAddedCalendars}
              setShowRecommendedCalendars={setShowRecommendedCalendars}
              sx={tagCalendarsListSx}
              fetchSearchByTagsNextPage={fetchSearchByTagsNextPage}
              hasMoreSearchByTagsPages={hasMoreSearchByTagsPages}
            />
          </>
        ) : (
          <CalendarsTagNotFound navigate={navigate} />
        )}
      </>
    )}
  </>
);

const CalendarsTagNotFound = ({ navigate }) => (
  <ActionCard
    title="No calendars found"
    description="There are no calendars associated with this tag."
    primaryButtonCTA="Search for calendars"
    primaryAction={() => navigate(getPageRoute("chat", "SEARCH"))}
    iconName="calendar_month"
    sx={{
      position: "static",
      mx: "auto",
      transform: "none",
      mt: { xs: 8, sm: 10 },
      pb: { xs: 16, sm: "80px" },
    }}
  />
);

function Home() {
  const { user, refetchAuth } = useContext(AuthContext);
  const [searchParams] = useSearchParams();
  let { tab } = searchParams;
  tab =
    tab || new URLSearchParams(window.location.search).get("tab") || "discover";
  const [activeTab, setActiveTab] = useState(tab);
  if (!tab || activeTab)
    window.history.replaceState({}, "", `?tab=${activeTab}`);
  const [needRefresh, setNeedRefresh] = useState(false);
  const navigate = useNavigate();
  const isUser = Boolean(user);
  const location = useLocation();
  const {
    setSnackbarOpen,
    setSnackbarMessage,
    setSnackbarSeverity,
    showSnackBar,
  } = useSnackbarContext();

  useEffect(() => {
    if (location.state?.calendarNotFound) {
      showSnackBar(
        setSnackbarOpen,
        setSnackbarMessage,
        setSnackbarSeverity,
        <>
          This calendar wasn't found. Try{" "}
          <Link
            as="span"
            sx={{ cursor: "pointer" }}
            onClick={() => navigate(getPageRoute("chat", "SEARCH"))}
          >
            searching
          </Link>{" "}
          for it{" "}
          <Icon
            baseClassName="material-symbols-outlined"
            sx={{
              verticalAlign: "text-bottom",
              cursor: "pointer",
              fontSize: "1rem",
            }}
            onClick={() => navigate(getPageRoute("chat", "SEARCH"))}
          >
            north_east
          </Icon>
        </>,
        "error"
      );
    }
  }, [location.state?.calendarNotFound]);

  const trendingTags = useMemo(() => Object.values(onboardingTags).flat(), []);
  const {
    refetchUserEvents,
    addedCalendars,
    setAddedCalendars,
    isAddedLoading,
    upcomingAddedEvents,
    initialLoad,
    setInitialLoad,
    isTrendingLoading,
    tagsFilter,
    setTagsFilter,
    isStaffPicks,
    subscriberAZCalendars,
    leaderboardCalendars,
    isLoadingCalendarsByTags,
    relatedTags,
    hasMoreSearchByTagsPages,
    fetchSearchByTagsNextPage,
    savedTags,
  } = useTrendingContent();

  const { mutateAsync: updateTags } = useUpdateTags();
  const { tag: urlTag } = useParams();

  // Get default set of trending tags
  const trendingCalendarTags = useMemo(
    () => [...new Set(trendingTags)],
    [trendingTags]
  );

  // Select the URL tag when navigating to /tag/:tag
  useEffect(() => {
    // Select the URL Tag.
    // Prevent bug: Don't select the url tag if user navigates back to the /tag/:tag URL.
    if (urlTag && LAST_URL_TAG_ADDED !== urlTag) {
      setTagsFilter((prevState) => {
        LAST_URL_TAG_ADDED = urlTag;
        return [urlTag];
      });
    }
  }, [urlTag]);

  // Handles selecting Trending or Added events tab.
  const handleTabChange = async (event, key) => {
    revealNavbar();
    if (needRefresh) {
      setInitialLoad(true);
      refetchUserEvents();
      setNeedRefresh(false);
    }
    setActiveTab(key);
  };

  const onSubscribeAction = (eventId, subscribed) => {
    setNeedRefresh(true);
  };

  const actionCardSx = {
    top: {
      xs: `calc(25vh + 60px + ${isUser ? "90px" : "0px"})`,
      sm: `calc(25vh + 80px + ${isUser ? "90px" : "0px"})`,
    },
    py: { sm: "80px" },
  };

  const tagCalendarsListSx = { borderTop: "none", "&>div": { pt: 0 } };

  const renderDiscoverTab = () => (
    <>
      {!isTrendingLoading && !initialLoad ? (
        <>
          <SearchBar initialFocus={true} />
          <TagsCollection
            trendingCalendarTags={trendingCalendarTags}
            tagsFilter={tagsFilter}
            setTagsFilter={setTagsFilter}
            savedTags={savedTags}
            isStaffPicks={isStaffPicks}
            isLoadingCalendarsByTags={isLoadingCalendarsByTags}
            urlTag={urlTag}
            manageCustomTags={true}
            sx={{ mt: 3 }}
          />
          <CalendarSections
            leaderboardCalendars={leaderboardCalendars}
            subscriberAZCalendars={subscriberAZCalendars}
            isLoadingCalendarsByTags={isLoadingCalendarsByTags}
            onSubscribeAction={onSubscribeAction}
            setAddedCalendars={setAddedCalendars}
            fetchSearchByTagsNextPage={fetchSearchByTagsNextPage}
            hasMoreSearchByTagsPages={hasMoreSearchByTagsPages}
            tagCalendarsListSx={tagCalendarsListSx}
            navigate={navigate}
          />
        </>
      ) : (
        <LoadingState />
      )}
    </>
  );

  const renderForYouTab = () => (
    <>
      {user ? (
        <>
          {isAddedLoading || initialLoad ? (
            <LoadingState />
          ) : (
            <>
              <TagsCollection
                trendingCalendarTags={savedTags?.sort() || []}
                tagsFilter={tagsFilter}
                setTagsFilter={setTagsFilter}
                savedTags={savedTags}
                isStaffPicks={isStaffPicks}
                isLoadingCalendarsByTags={isLoadingCalendarsByTags}
                relatedTags={relatedTags}
                activeTab={activeTab}
                sx={{ mt: 3 }}
              />
              <CalendarSections
                leaderboardCalendars={leaderboardCalendars}
                subscriberAZCalendars={subscriberAZCalendars}
                isLoadingCalendarsByTags={isLoadingCalendarsByTags}
                onSubscribeAction={onSubscribeAction}
                setAddedCalendars={setAddedCalendars}
                fetchSearchByTagsNextPage={fetchSearchByTagsNextPage}
                hasMoreSearchByTagsPages={hasMoreSearchByTagsPages}
                tagCalendarsListSx={tagCalendarsListSx}
                navigate={navigate}
              />
            </>
          )}
        </>
      ) : (
        <SignInCard
          navigate={navigate}
          actionCardSx={actionCardSx}
          tab="foryou"
        />
      )}
    </>
  );

  const renderUpcomingTab = () => (
    <>
      {user ? (
        <>
          {isAddedLoading || initialLoad ? (
            <LoadingState />
          ) : (
            <>
              {upcomingAddedEvents?.length > 0 ? (
                <InfiniteScrollList threshold={700} id="upcoming">
                  {upcomingAddedEvents.map((event, index) => (
                    <Event
                      calendar={event.calendar}
                      type="added"
                      key={
                        event.event.jCal[1].find((prop) => prop[0] === "uid")[3]
                      }
                      event={event.event}
                      calendarId={event.calendarId}
                      showUpload={false}
                      onSubscribeAction={() => refetchUserEvents()}
                      position={`added-${index}`}
                    />
                  ))}
                </InfiniteScrollList>
              ) : (
                <ActionCard
                  title="No upcoming events"
                  description="You haven't added any calendars yet."
                  testid="user-added-no-events"
                  primaryButtonCTA="Discover Calendars"
                  primaryAction={() => setActiveTab("discover")}
                  iconName="event"
                  sx={actionCardSx}
                />
              )}
            </>
          )}
        </>
      ) : (
        <SignInCard
          navigate={navigate}
          actionCardSx={actionCardSx}
          tab="upcoming"
        />
      )}
    </>
  );

  return (
    <div>
      <Helmet>
        <title>Stanza - Discover and add events to your calendar</title>
        <meta
          name="description"
          content="We're an AI-powered platform for creating, discovering & subscribing to community calendars. Find events and calendars across sports, TV, entertainment, local events and more."
        />
      </Helmet>
      <Navbar user={user} />
      <Box
        sx={{
          margin: "auto",
          maxWidth: { xs: "100%", sm: "600px" },
          p: { xs: 2, md: "1rem 2rem" },
        }}
      >
        <Hero user={user} />
        <Tabs
          id="home-tabs"
          data-testid="home-tabs"
          value={activeTab}
          onChange={handleTabChange}
          variant={"fullWidth"}
          sx={{ zIndex: 10 }}
        >
          <Tab label="Discover" value="discover" disableRipple />
          <Tab label="For You" value="foryou" disableRipple />
          <Tab label="Upcoming" value="upcoming" disableRipple />
        </Tabs>

        <Box role="tabpanel" hidden={activeTab !== "discover"}>
          {renderDiscoverTab()}
        </Box>

        <Box role="tabpanel" hidden={activeTab !== "foryou"}>
          {renderForYouTab()}
        </Box>

        <Box role="tabpanel" hidden={activeTab !== "upcoming"}>
          {renderUpcomingTab()}
        </Box>
      </Box>
    </div>
  );
}

export default memo(Home);
